import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Users,
  Gift,
  Sparkles,
  Copy,
  Check,
  Share2,
  Wallet,
  TrendingUp,
  Banknote,
  Percent,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", legal_pro: "Legal Pro", business: "Business" };

function maskUserId(uid) {
  if (!uid) return "";
  if (uid.length <= 8) return uid;
  return uid.slice(0, 4) + "…" + uid.slice(-4);
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function Referrals() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useImperativeLoad(() => {
    api.get("/referrals").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return <div className="flex h-full items-center justify-center text-slate-400">{t("common.loading")}</div>;
  }

  const referralLink = `${window.location.origin}/auth?mode=signup&ref=${data.referral_code}`;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(t("referrals.copied"));
    setTimeout(() => setCopied(false), 1500);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "NEURACOM by TelcaVoIP",
          text: "Join me on NEURACOM — AI workspace for business.",
          url: referralLink,
        });
      } catch (e) { /* ignore */ }
    } else {
      handleCopy(referralLink);
    }
  };

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="referrals-page">
      <header className="mb-8 max-w-3xl">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#9ACD32]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#9ACD32]">
          <Gift className="h-3 w-3" />
          NEURACOM AFFILIATE
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">
          {t("referrals.title")}
        </h1>
        <p className="mt-3 text-slate-300">{t("referrals.subtitle")}</p>
      </header>

      {/* KPI cards */}
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={Users} label={t("referrals.stats_invited")} value={data.total_referred} testId="stat-invited" />
        <StatCard icon={Sparkles} label={t("referrals.stats_paying")} value={data.total_rewarded} testId="stat-rewarded" accent />
        <StatCard icon={Wallet} label={t("referrals.stats_credit_balance")} value={`€${(data.referral_credit_balance_eur || 0).toFixed(2)}`} testId="stat-credit-balance" accent />
        <StatCard icon={TrendingUp} label={t("referrals.stats_credit_lifetime")} value={`€${(data.referral_credit_lifetime_eur || 0).toFixed(2)}`} testId="stat-credit-lifetime" />
        <StatCard icon={Gift} label={t("referrals.stats_bonus")} value={data.referral_bonus_months} suffix="mo" testId="stat-bonus" />
      </div>

      {/* How it works */}
      <Card className="mt-8 border-[#9ACD32]/30 bg-gradient-to-br from-[#9ACD32]/5 to-transparent p-6" data-testid="affiliate-program-card">
        <h2 className="text-base font-bold uppercase tracking-wider text-[#9ACD32]">
          {t("referrals.program_title")}
        </h2>
        <ol className="mt-4 space-y-3 text-sm text-slate-200">
          <li className="flex items-start gap-3"><Step n={1} /> {t("referrals.program_step1")}</li>
          <li className="flex items-start gap-3"><Step n={2} /> {t("referrals.program_step2")}</li>
          <li className="flex items-start gap-3"><Step n={3} /> <span><Banknote className="mr-1 inline h-4 w-4 text-[#9ACD32]" />{t("referrals.program_step3").replace("{credit}", (data.conversion_credit_eur || 20).toFixed(0))}</span></li>
          <li className="flex items-start gap-3"><Step n={4} /> <span><Percent className="mr-1 inline h-4 w-4 text-[#9ACD32]" />{t("referrals.program_step4")}</span></li>
        </ol>
        <p className="mt-4 border-t border-slate-800 pt-4 text-xs text-slate-400">
          {t("referrals.payout_hint").replace(
            "{sales}",
            ""
          )}
          <Link to="/marketplace/sales" className="text-[#9ACD32] underline">
            {t("referrals.payout_hint_link")}
          </Link>
        </p>
      </Card>

      <Card className="mt-8 border-slate-800 bg-[#0A0C1F] p-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{t("referrals.your_link")}</p>
        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
          <Input
            readOnly
            value={referralLink}
            className="flex-1 border-slate-700 bg-slate-900 font-mono text-sm text-slate-200"
            data-testid="referral-link-input"
          />
          <Button
            onClick={() => handleCopy(referralLink)}
            className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="referral-copy-btn"
          >
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? t("referrals.copied") : t("referrals.copy")}
          </Button>
          <Button
            onClick={handleShare}
            variant="outline"
            className="rounded-full border-slate-700 text-slate-200"
            data-testid="referral-share-btn"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {t("referrals.your_code")}: <span className="font-mono text-[#9ACD32]" data-testid="referral-code">{data.referral_code}</span>
        </p>
      </Card>

      {/* Recent marketplace commissions */}
      <section className="mt-10" data-testid="commissions-section">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("referrals.commissions_title")}
        </h2>
        {(!data.recent_commissions || data.recent_commissions.length === 0) ? (
          <p className="text-sm text-slate-500" data-testid="commissions-empty">{t("referrals.commissions_empty")}</p>
        ) : (
          <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3">{t("referrals.commissions_col_buyer")}</th>
                  <th className="px-5 py-3">{t("referrals.commissions_col_price")}</th>
                  <th className="px-5 py-3">{t("referrals.commissions_col_commission")}</th>
                  <th className="px-5 py-3">{t("referrals.commissions_col_date")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_commissions.map((c, i) => (
                  <tr key={c.purchase_id} className="border-b border-slate-800/60" data-testid={`commission-row-${i}`}>
                    <td className="px-5 py-3 font-mono text-slate-300">{maskUserId(c.buyer_user_id)}</td>
                    <td className="px-5 py-3 font-mono text-slate-300">€{c.price_eur}</td>
                    <td className="px-5 py-3 font-mono font-bold text-[#9ACD32]">+€{(c.referrer_commission_eur || 0).toFixed(2)}</td>
                    <td className="px-5 py-3 text-slate-400">{formatDate(c.paid_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      {/* Invited users */}
      <section className="mt-10">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("referrals.table_title")}
        </h2>
        {data.referred_users.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="referrals-empty">{t("referrals.no_invites")}</p>
        ) : (
          <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3">{t("referrals.col_user")}</th>
                  <th className="px-5 py-3">{t("referrals.col_tier")}</th>
                  <th className="px-5 py-3">{t("referrals.col_status")}</th>
                  <th className="px-5 py-3">{t("referrals.col_eligible")}</th>
                  <th className="px-5 py-3">{t("referrals.col_date")}</th>
                </tr>
              </thead>
              <tbody>
                {data.referred_users.map((u, i) => (
                  <tr key={i} className="border-b border-slate-800/60" data-testid={`referral-row-${i}`}>
                    <td className="px-5 py-3 font-mono text-slate-300">{u.email_masked}</td>
                    <td className="px-5 py-3">
                      <span className={`tier-badge tier-${u.tier}`}>{TIER_LABEL[u.tier] || u.tier}</span>
                    </td>
                    <td className="px-5 py-3">
                      {u.rewarded ? (
                        <span className="inline-flex items-center gap-1 text-[#9ACD32] font-semibold">
                          <Check className="h-3.5 w-3.5" />
                          {t("referrals.status_rewarded")}
                        </span>
                      ) : (
                        <span className="text-slate-500">{t("referrals.status_pending")}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-400">
                      {u.eligible_until ? (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {t("referrals.until").replace("{date}", formatDate(u.eligible_until))}
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-400">{formatDate(u.signed_up_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </div>
  );
}

function Step({ n }) {
  return (
    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#9ACD32] font-mono text-xs font-extrabold text-[#05073B]">
      {n}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, suffix, testId, accent }) {
  return (
    <Card
      className={`border-slate-800 bg-[#0A0C1F] p-5 ${accent ? "ring-1 ring-[#9ACD32]/30" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-[#9ACD32]" : "text-slate-500"}`} />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-2xl font-extrabold text-white lg:text-3xl">{value}</span>
        {suffix && <span className="text-sm text-slate-500">{suffix}</span>}
      </div>
    </Card>
  );
}
